import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { translations, DEFAULT_LOCALE, SUPPORTED_LOCALES, RTL_LOCALES } from "./translations";

const STORAGE_KEY = "tbs.locale";

const I18nContext = createContext({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  t: (key) => key,
  dict: translations[DEFAULT_LOCALE],
  isRTL: false,
});

function getInitialLocale() {
  try {
    const stored = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    if (stored && SUPPORTED_LOCALES.includes(stored)) return stored;
    if (typeof navigator !== "undefined") {
      const lang = (navigator.language || "").toLowerCase();
      const code = lang.split("-")[0];
      if (SUPPORTED_LOCALES.includes(code)) return code;
    }
  } catch (e) {
    // ignore
  }
  return DEFAULT_LOCALE;
}

function resolvePath(obj, path) {
  if (!path) return undefined;
  const parts = path.split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

export const I18nProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(getInitialLocale);

  const isRTL = RTL_LOCALES.includes(locale);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, locale);
      if (typeof document !== "undefined") {
        document.documentElement.lang = locale;
        document.documentElement.dir = isRTL ? "rtl" : "ltr";
      }
    } catch (e) {
      // ignore
    }
  }, [locale, isRTL]);

  const setLocale = useCallback((next) => {
    if (SUPPORTED_LOCALES.includes(next)) setLocaleState(next);
  }, []);

  const dict = translations[locale] || translations[DEFAULT_LOCALE];
  const fallbackDict = translations.en || translations[DEFAULT_LOCALE];

  const t = useCallback(
    (key, fallback) => {
      const val = resolvePath(dict, key);
      if (val !== undefined && val !== null) return val;
      // Try EN fallback
      const enVal = resolvePath(fallbackDict, key);
      if (enVal !== undefined && enVal !== null) return enVal;
      return fallback ?? key;
    },
    [dict, fallbackDict]
  );

  // Helper that returns full sub-tree; falls back to EN when missing.
  const tree = useCallback(
    (path) => {
      const node = resolvePath(dict, path);
      if (node !== undefined && node !== null) return node;
      return resolvePath(fallbackDict, path);
    },
    [dict, fallbackDict]
  );

  const value = useMemo(
    () => ({ locale, setLocale, t, tree, dict, fallbackDict, isRTL }),
    [locale, setLocale, t, tree, dict, fallbackDict, isRTL]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
